'use client';

import { Stack } from '@mui/material';
import { TCustomerResponse, TValidationResults } from '@verifime/api-definition';
import {
  ADDRESS_PREFIX_SEPARATOR,
  Australia,
  SERVER_ERRORS,
  SupportedCountryCode,
  TAddress,
  TCustomer,
  TObject,
  basicFieldsPrefix,
} from '@verifime/utils';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import {
  ErrorScope,
  GenerateFormFieldsV3,
  TErrors,
  TFormFieldsAndRulesV3,
  generateNonFieldErrors,
  getFieldsNamesFromFieldsAndRulesV3,
  getFieldsRulesV3,
  showFieldsErrors,
  useCustomForm,
} from '../../Form';
import useAddressFieldsData, { TDefaultAddressSource } from './useAddressFieldsData';
import FormAddressFields, { TAddressView } from './FormAddressFields';
import {
  AVAILABLE_COUNTRIES,
  AddressFieldsAndRulesName,
  preferredContactNumberFieldName,
} from './utils';

export type TAddEntityAddressV2 = {
  renderType: 'AddressOnly' | 'AddressWithCountrySelect';
  fieldsAndRulesName: AddressFieldsAndRulesName;
  fieldsPrefix: string;
  label?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  sameAsAddress?: { addressFieldsAndRulesName: AddressFieldsAndRulesName; label: string };
};
export type TAddEntityAddressesV2 = TAddEntityAddressV2[];

export default function useAddEntityV2({
  initialFormFieldsAndRules,
  submitApiRequest,
  retrieveCustomerApiRequest,
  customerId,
  prefill = false,
  initialAddresses = [],
  AddressView,
  fieldsNotChangedCallback,
  onSubmitComplete,
  onRetrieveComplete,
}: {
  initialFormFieldsAndRules: TFormFieldsAndRulesV3;
  submitApiRequest: (
    formVerificationInfo: Record<string, any>,
    defaultCustomer?: TCustomer,
  ) => Promise<TCustomerResponse>;
  retrieveCustomerApiRequest?: (customerId?: string) => Promise<TCustomer>;
  customerId?: string;
  /** When true, calls retrieveCustomerApiRequest without a customerId to pre-populate the form */
  prefill?: boolean;
  initialAddresses?: TAddEntityAddressesV2;
  /**
   * Dependency-injected address capture view. In production, callers pass
   * `AddressAutocompleteView` from `@verifime/views`; tests and stories can
   * substitute a stub that conforms to the `TAddressView` contract.
   *
   * We inject rather than import the view because `@verifime/views` depends
   * on `@verifime/components`, so a direct import here would create a cyclic
   * package dependency.
   */
  AddressView: TAddressView;
  fieldsNotChangedCallback?: () => void;
  onSubmitComplete?: (customer: TCustomer, reason?: any, formData?: TObject) => void;
  onRetrieveComplete?: (customer: TCustomer | null, isPrefill?: boolean) => void;
}) {
  const [nonFieldErrors, setNonFieldErrors] = useState<TErrors>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetriving, setIsRetriving] = useState(false);

  const [defaultCustomer, setDefaultCustomer] = useState<TCustomer | null>(null);
  // Address data for form population, set from both existing-customer and prefill paths.
  // Separated from defaultCustomer which drives the create-vs-update submit decision.
  const [defaultAddressSource, setDefaultAddressSource] =
    useState<TDefaultAddressSource | null>(null);
  const [isFieldsChanged, setIsFieldsChanged] = useState(false);
  const [prevFormFieldsAndRules, setPrevFormFieldsAndRules] =
    useState<TFormFieldsAndRulesV3 | null>(null);
  const [prevAddresses, setPrevAddresses] = useState<TAddEntityAddressesV2 | null>(null);
  const [prevSelectedCountryCode, setPrevSelectedCountryCode] =
    useState<SupportedCountryCode | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState(AVAILABLE_COUNTRIES[0].code);

  const memoizedAddresses = useRef(initialAddresses ?? []);
  memoizedAddresses.current = initialAddresses ?? [];
  const formFieldsAndRules = useRef(initialFormFieldsAndRules);
  formFieldsAndRules.current = initialFormFieldsAndRules;

  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
    unregister,
    watch,
    setError,
    reset,
    getFieldState,
    updateSchema,
  } = useCustomForm();

  const onAddressChange = useCallback(() => setIsFieldsChanged(true), []);

  const { addressesSchemaContribution, defaultAddressFieldsValues } =
    useAddressFieldsData({
      addresses: initialAddresses,
      selectedCountryCode,
      defaultAddressSource,
    });

  // One-shot seeding: when the default address values first become available
  // (after retrieveCustomerApiRequest resolves, or synchronously when a record
  // is already in state), write them into RHF so the injected AddressView's
  // `value` reflects the customer record / prefill.
  //
  // Two guards keep this from clobbering user edits:
  //   1. A ref ensures the seed runs at most once per mount (blocks re-seeding
  //      when `defaultAddressFieldsValues` recomputes on e.g. a country switch
  //      A->B->A round-trip).
  //   2. Per-field `isDirty` check blocks the common prefill race: user opens
  //      the form and starts typing before `retrieveCustomerApiRequest`
  //      resolves, then the seed arrives and would otherwise silently
  //      overwrite what the user just typed.
  const addressSeededRef = useRef(false);
  useEffect(() => {
    if (addressSeededRef.current) return;
    const entries = Object.entries(defaultAddressFieldsValues);
    if (entries.length === 0) return;
    for (const [fieldName, fieldValue] of entries) {
      if (!getFieldState(fieldName).isDirty) {
        setValue(fieldName, fieldValue);
      }
    }
    addressSeededRef.current = true;
  }, [defaultAddressFieldsValues, setValue, getFieldState]);

  // Set default phone number country dial code when customerId is not provided
  //
  // Customer Record Context: The absence of customerId means the system doesn't have any
  // existing record for the user's individual customer, so we are free to add the desired
  // default and encourage customer to actually provide their contact number. However, if we
  // already have customer record (i.e., the customer has completed step 1), then we respect
  // customer's choice of whether to provide their contact number or not.
  //
  // Default Country: Australia is the default country of residence for this application.
  useEffect(() => {
    if (!customerId) {
      setValue(preferredContactNumberFieldName, `${Australia.dialCode} `);
    }
  }, [customerId]);

  useEffect(() => {
    if (
      // We don't consider functions when comparing equalities
      !isEqual(
        JSON.stringify(prevFormFieldsAndRules || {}),
        JSON.stringify(initialFormFieldsAndRules || {}),
      ) ||
      !isEqual(prevAddresses, initialAddresses) ||
      selectedCountryCode !== prevSelectedCountryCode
    ) {
      setPrevFormFieldsAndRules(initialFormFieldsAndRules);
      setPrevAddresses(initialAddresses);
      setPrevSelectedCountryCode(selectedCountryCode);

      updateSchema(
        { ...getFieldsRulesV3(initialFormFieldsAndRules), ...addressesSchemaContribution },
        { resetSchema: true },
      );
    }
  }, [
    initialFormFieldsAndRules,
    prevFormFieldsAndRules,
    updateSchema,
    initialAddresses,
    prevAddresses,
    selectedCountryCode,
    prevSelectedCountryCode,
    addressesSchemaContribution,
    unregister,
    setValue,
  ]);

  const renderFormRef = useRef({ errors });
  renderFormRef.current.errors = errors;

  const retrievingRef = useRef(false);
  useEffect(() => {
    if (retrievingRef.current || !retrieveCustomerApiRequest || (!customerId && !prefill)) {
      return;
    }

    setIsRetriving(true);
    retrievingRef.current = true;
    retrieveCustomerApiRequest(customerId)
      .then((customer: TObject) => {
        // null means no prefill data available (e.g. 204 response). Silently degrade.
        // onRetrieveComplete is intentionally not called; callers should not assume it always fires.
        if (!customer) return;
        // We currently only support one country in a form,
        // even there are multiple addresses in the form (i.e. Registered address & Principal place of business).
        // That means multiple addresses are all from the same country,
        // thus we only grab the country code of the first address as the country code of form.
        const addressFieldName = memoizedAddresses.current[0]?.fieldsPrefix?.replace(
          ADDRESS_PREFIX_SEPARATOR,
          '',
        );

        // It may be `undefined` due to `Public company` does not have address for now,
        // or when the customer object is a prefill response that contains no address data.
        if (addressFieldName && customer[addressFieldName]) {
          setSelectedCountryCode(customer[addressFieldName].countryCode);
        }
        let toBeReset = {};
        const formFieldsNames = getFieldsNamesFromFieldsAndRulesV3(formFieldsAndRules.current);

        formFieldsNames.forEach((fieldName) => {
          const name = fieldName
            .replace(basicFieldsPrefix, '')
            .replace(/^./, (c) => c.toLowerCase());

          // Hard code for now
          // We may no need this parts once our API supports no need to specify
          // the countryCode but reading countryCode from the address fields of form
          if (name === 'countryCode') {
            toBeReset = { ...toBeReset, [fieldName]: customer[addressFieldName]?.countryCode };
          } else {
            toBeReset = { ...toBeReset, [fieldName]: customer[name] };
          }
        });

        // Fill form fields except addresses by values
        reset(toBeReset);
        // Address defaults: extract all address sub-objects from the response.
        // Organisations may have multiple (e.g. registeredOffice, principalPlaceOfBusiness).
        const addressSource: Record<string, TAddress> = {};
        memoizedAddresses.current.forEach((addr) => {
          const fieldName = addr.fieldsPrefix.split(ADDRESS_PREFIX_SEPARATOR)[0];
          if (fieldName && customer[fieldName]) {
            addressSource[fieldName] = customer[fieldName];
          }
        });
        if (Object.keys(addressSource).length > 0) {
          // Type assertion required: dynamic key construction from fieldsPrefix strings
          // prevents TS from inferring the union shape. Eliminating this requires making
          // useAddEntity generic over its address fields; tracked in TD-009.
          setDefaultAddressSource(addressSource as TDefaultAddressSource);
        }
        // Existing record tracking: only set when customer has an id (drives create-vs-update on submit)
        if (customer?.id) {
          setDefaultCustomer(customer);
        }
        onRetrieveComplete?.(customer, prefill);
      })
      .catch((err: any) => {
        const { code, message } = err || { code: 'default', message: SERVER_ERRORS.default };
        setNonFieldErrors([{ code, message, scope: ErrorScope.Form }]);
        // Unlike the null-return path above, errors DO call onRetrieveComplete(null).
        // isPrefill is omitted (undefined) so callers can distinguish error from silent 204.
        onRetrieveComplete?.(null);
      })
      .finally(() => {
        setIsRetriving(false);
      });
  }, [customerId, prefill, onRetrieveComplete, reset, retrieveCustomerApiRequest]);

  const onDetailsSubmit: SubmitHandler<any> = async (verificationInfo) => {
    if (typeof fieldsNotChangedCallback === 'function') {
      // No any change, directly go next step
      if (!isFieldsChanged) {
        fieldsNotChangedCallback();
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const result = await submitApiRequest(verificationInfo, defaultCustomer);

      const { customer, validationResults = [] } = result || {
        customer: {},
        validationResults: [],
      };
      const typedValidationResults = validationResults as TValidationResults;

      if (typedValidationResults.length > 0) {
        showFieldsErrors(typedValidationResults, setError, {
          basicFieldsPrefix,
          isAddPrefix: true,
        });
        const nonFieldErrors = generateNonFieldErrors(typedValidationResults);
        setNonFieldErrors(nonFieldErrors);
        return;
      }

      setDefaultCustomer(null);
      setDefaultAddressSource(null);
      onSubmitComplete?.(customer, null, verificationInfo);

      // Reset form fields when submit successfully. This ensures the form is clean for next use.
      reset();
    } catch (err: any) {
      const { code, message } = err || { code: 'default', message: SERVER_ERRORS.default };
      setNonFieldErrors([{ code, message, scope: ErrorScope.Form }]);
      onSubmitComplete?.(null, err, verificationInfo);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsOperations = useMemo(() => {
    const fieldsOperations: Record<string, any> = {};
    for (const [_, { fields }] of Object.entries(formFieldsAndRules.current || {})) {
      if (!fields || fields.length < 1) {
        continue;
      }
      for (const fieldInfo of fields) {
        switch (fieldInfo.renderType) {
          case 'Text':
            fieldsOperations[fieldInfo.fieldName] = {
              onTextChange: () => setIsFieldsChanged(true),
            };
            break;
          case 'File':
            fieldsOperations[fieldInfo.fieldName] = {
              onFileChange: () => setIsFieldsChanged(true),
            };
            break;
          case 'Checkbox':
            fieldsOperations[fieldInfo.fieldName] = {
              onCheckBoxChange: () => setIsFieldsChanged(true),
            };
            break;
          case 'Date':
            fieldsOperations[fieldInfo.fieldName] = {
              onDateChange: () => setIsFieldsChanged(true),
            };
            break;
          case 'DateRange':
            fieldsOperations[fieldInfo.fieldName] = {
              onDateRangeChange: () => setIsFieldsChanged(true),
            };
            break;
          case 'Select':
            fieldsOperations[fieldInfo.fieldName] = {
              onOptionChange: () => setIsFieldsChanged(true),
              initValue: fieldInfo.items?.[0]?.code,
            };
            break;
          default:
            break;
        }
      }
    }
    return fieldsOperations;
  }, []);

  const submitForm = handleSubmit(onDetailsSubmit);

  // Hold the FormAddressFields inputs in a ref so FormRenders stays stable
  // across renders. FormRenders' function identity drives whether React
  // remounts the entire form subtree, so we must not add render-volatile
  // values (addresses array literal, memoised derivations, prev-country
  // state) to its dependency list.
  const formAddressFieldsPropsRef = useRef({
    addresses: initialAddresses,
    selectedCountryCode,
    prevSelectedCountryCode,
    AddressView,
  });
  formAddressFieldsPropsRef.current = {
    addresses: initialAddresses,
    selectedCountryCode,
    prevSelectedCountryCode,
    AddressView,
  };

  const FormRenders = useCallback(() => {
    const {
      addresses,
      selectedCountryCode: currentCountryCode,
      prevSelectedCountryCode: currentPrevCountryCode,
      AddressView: CurrentAddressView,
    } = formAddressFieldsPropsRef.current;
    return (
      <Stack width="100%" direction="column">
        <GenerateFormFieldsV3
          formFieldsAndRules={formFieldsAndRules.current}
          control={control}
          errors={renderFormRef.current.errors}
          fieldsOperations={fieldsOperations}
        />
        <FormAddressFields
          addresses={addresses}
          selectedCountryCode={currentCountryCode}
          prevSelectedCountryCode={currentPrevCountryCode}
          setSelectedCountryCode={setSelectedCountryCode}
          setValue={setValue}
          unregister={unregister}
          watch={watch}
          onAddressChange={onAddressChange}
          AddressView={CurrentAddressView}
        />
      </Stack>
    );
  }, [control, fieldsOperations, setValue, unregister, watch, onAddressChange]);

  return {
    FormRenders,
    nonFieldErrors,
    isSubmitting,
    isRetriving,
    submitForm,
    errors: renderFormRef.current.errors,
    reset,
    setDefaultCustomer,
  };
}
